// libs
import React, { useEffect, useState } from 'react'

// interfaces / constants
import { IErrorResponse } from 'src/api/interfaces/errors'
import { CLASS_PREFIX } from 'src/constants/'
import { MESSAGE_BOX_TIMEOUT } from 'src/constants/timeouts'
import { COLOR_TYPE_GRAY_TINT_9 } from 'src/utils/color'

// classes / components
import LegacyButton, {
  BUTTON_TYPE_CONTAINED,
  BUTTON_TYPE_TEXT
} from 'src/components/forms/button/button'
import FieldSet from 'src/components/forms/field_set/field_set'
import InputField from 'src/components/forms/input_field/input_field'
import MessageBoxWithModal from 'src/components/message_box/message_box_with_modal'
import { IconType } from 'src/components/message_segment/message_segment'
import MessageSegmentWithModal from 'src/components/message_segment/message_segment_with_modal'
import SnackBar from 'src/components/snack_bar/snack_bar'

// helpers
import { textResources } from 'src/lang/de'
import { getFirstFullMessageGQL } from 'src/utils/api_error_message/api_error_message'
import { isNotEmpty, validateEmail } from 'src/utils/validation'

import { useFeatureSettings } from '@lopo-tech/frontends-shared'
import { useCreateFeedback } from '@lopo-tech/lopo-api'
import './message_form.scss'

const labels = textResources.feedback

interface IProps {
  additionalParams?: string;
  body: string;
  close: () => void;
  title: string;
  loggedInUserEmail?: string;
  placeholder?: string;
  successImage: IconType;
  successTitle: string;
  successBody: string | JSX.Element;
}

const cls: string = CLASS_PREFIX + 'message-form'
// MESSAGE_MAX_LENGTH must be equal or lower than our Backend-validation.
// To fit also the additional (hidden) params into the message, we need to leave a buffer.
// The additional params have up to approximately 100 characters.
const MESSAGE_MAX_LENGTH = 2000

const MessageForm: React.FC<IProps> = ({
  additionalParams,
  body,
  close,
  loggedInUserEmail,
  placeholder,
  successImage,
  successTitle,
  successBody,
  title
}) => {
  const [submitFeedback, feedback] = useCreateFeedback()
  const [email, setEmail] = useState(loggedInUserEmail || '')
  const [emailIsValid, setEmailIsValid] = useState(true)
  const [formSubmitted, setFormSubmitted] = useState(false)
  const [message, setMessage] = useState('')
  const [messageIsValid, setMessageIsValid] = useState(true)
  const [showSnackbar, setShowSnackbar] = useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState('')
  const [successTimeout, setSuccessTimeout] = useState<number | null>(null)

  const termsMail = useFeatureSettings().supportEmail

  useEffect(() => {
    return () => {
      // Cleanup timeout on unmount
      if (successTimeout) {
        clearTimeout(successTimeout)
      }
    }
  }, [successTimeout])

  const changeEmail = (value: string) => {
    setEmail(value)
    setEmailIsValid(true)
  }

  const changeMessage = (value: string) => {
    setMessage(value)
    setMessageIsValid(true)
  }

  const hideSnackbar = () => {
    setShowSnackbar(false)
  }

  const submitForm = () => {
    const validEmail = validateEmail(email)
    const validMessage = !!message && isNotEmpty(message)

    if (!validEmail) {
      setEmailIsValid(false)
    }

    if (!validMessage) {
      setMessageIsValid(false)
    }

    if (validEmail && validMessage) {
      // Add hidden message like params, context, etc
      const feedbackMessage = additionalParams
        ? message + additionalParams
        : message
      submitFeedback({
        variables: { input: { email, message: feedbackMessage } }
      })
        .then(() => {
          setFormSubmitted(true)
          const timeout = window.setTimeout(() => close(), MESSAGE_BOX_TIMEOUT)
          setSuccessTimeout(timeout)
        })
        .catch((error: IErrorResponse) => {
          const snackbarMessage = getFirstFullMessageGQL(error) ?? labels.errorSending
          setShowSnackbar(true)
          setSnackbarMessage(snackbarMessage)
        })
    }
  }

  const renderFeedbackForm = () => {
    const maxLength = additionalParams
      ? MESSAGE_MAX_LENGTH - additionalParams.length
      : MESSAGE_MAX_LENGTH

    return (
      <MessageBoxWithModal
        isLoading={feedback.loading}
        onClose={close}
        title={title}
        whiteBackground
      >
        <p className={cls + '__body'}>{body}</p>
        <div className={cls + '__input'}>
          <FieldSet>
            {!loggedInUserEmail && (
              <InputField
                name="email"
                label={labels.emailPlaceholder}
                onChange={changeEmail}
                value={email}
                valid={emailIsValid}
                errorText={textResources.shared.formErrorInvalid}
                required
                type="email"
              />
            )}
            <InputField
              name="message"
              label={placeholder || labels.messagePlaceholder}
              onChange={changeMessage}
              value={message}
              valid={messageIsValid}
              maxLength={maxLength}
              required
            />
          </FieldSet>
        </div>
        <LegacyButton
          variant={BUTTON_TYPE_CONTAINED}
          color={COLOR_TYPE_GRAY_TINT_9}
          label={
            loggedInUserEmail
              ? textResources.wizard.send
              : labels.acceptAndSubmit
          }
          fullWidth
          onClick={submitForm}
          lowerCase
          primary={true}
        />
        {!loggedInUserEmail && (
          <p className={cls + '__terms'}>{labels.terms(termsMail)}</p>
        )}
        <div className={cls + '__footer'}>
          <LegacyButton
            variant={BUTTON_TYPE_TEXT}
            fullWidth
            onClick={close}
            label={textResources.shared.cancel}
            lowerCase
          />
        </div>
      </MessageBoxWithModal>
    )
  }

  const renderFeedbackThanks = () => {
    return (
      <MessageSegmentWithModal
        image={successImage}
        title={successTitle}
        description={successBody}
        onClose={close}
      />
    )
  }

  return (
    <>
      {showSnackbar && (
        <SnackBar showClose onClose={hideSnackbar}>
          {snackbarMessage}
        </SnackBar>
      )}
      {!formSubmitted ? renderFeedbackForm() : renderFeedbackThanks()}
    </>
  )
}

export default MessageForm
